<template>
  <v-card
    class="ma-0 px-1 app-form-page"
    :class="{
      'app-form-page--fixed': !isEmbedded && !isMobile,
      'app-form-page--mobile': isMobile && !isEmbedded,
      'app-form-page--embedded': isEmbedded,
    }"
    width="100%"
    flat
  >
    <!-- Mobile header (title only; tabs + type chips below) -->
    <div v-if="isMobile && !isEmbedded" class="amf-header">
      <div class="amf-header__bar">
        <div class="amf-header__bar-actions">
          <button
            v-if="id"
            type="button"
            class="amf-header__btn amf-header__btn--create"
            :aria-label="$t(type)"
            @click="goTo(type)"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="amf-header__btn"
            :aria-label="$t('transferList')"
            @click="goTo('transferList')"
          >
            <v-icon size="20" color="#37474F">mdi-format-list-numbered</v-icon>
          </button>
        </div>
        <h1 class="amf-header__title">{{ $t("transfer") }}</h1>
        <button
          type="button"
          class="amf-header__btn amf-header__btn--back"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>
    </div>

    <!-- Desktop / embedded toolbar -->
    <v-app-bar
      v-else
      flat
      class="my-0 app-form-toolbar"
      :class="
        isMobile
          ? 'mb-2 py-1 px-2 app-form-toolbar--mobile'
          : 'mb-0 py-0 px-2'
      "
      dense
      :height="!isEmbedded && !isMobile ? 40 : undefined"
    >
      <v-toolbar-title class="app-form-toolbar__title me-3">
        {{ $t("transfer") }}
      </v-toolbar-title>
      <v-radio-group
        v-model="type"
        class="app-form-toolbar__type mt-0"
        dense
        hide-details
        row
      >
        <v-radio
          :label="$t('fromDebtor')"
          value="receivable"
          :disabled="id ? true : false"
        />
        <v-radio
          :label="$t('fromCreditor')"
          value="payable"
          :disabled="id ? true : false"
        />
      </v-radio-group>
      <v-spacer />
      <div class="app-form-toolbar__actions">
        <v-tooltip v-if="id" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(type)"
            >
              <v-icon color="teal"> mdi-plus-box-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(type) }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo('transferList')"
            >
              <v-icon color="primary"> mdi-format-list-numbered </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("transferList") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="$router.go(-1)"
            >
              <v-icon color="primary"> mdi-arrow-left </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("back") }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>
    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />
    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="app-form-page__form"
      lazy-validation
    >
      <div v-if="isMobile && !isEmbedded" class="amf-mobile-nav" dir="rtl">
        <div class="amf-mobile-nav__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="amf-mobile-nav__tab"
            :class="{ 'amf-mobile-nav__tab--active': formTab === 0 }"
            :aria-selected="formTab === 0"
            @click="formTab = 0"
          >
            {{ $t("public-information") }}
          </button>
          <button
            type="button"
            role="tab"
            class="amf-mobile-nav__tab"
            :class="{ 'amf-mobile-nav__tab--active': formTab === 1 }"
            :aria-selected="formTab === 1"
            :disabled="!documentPermmission"
            @click="documentPermmission && (formTab = 1)"
          >
            {{ $t("documents") }}
          </button>
        </div>
        <div class="amf-header__type">
          <button
            type="button"
            class="amf-type-chip"
            :class="{ 'amf-type-chip--active': type === 'receivable' }"
            :disabled="!!id"
            @click="type = 'receivable'"
          >
            {{ $t("fromDebtor") }}
          </button>
          <button
            type="button"
            class="amf-type-chip"
            :class="{ 'amf-type-chip--active': type === 'payable' }"
            :disabled="!!id"
            @click="type = 'payable'"
          >
            {{ $t("fromCreditor") }}
          </button>
        </div>
      </div>

      <v-tabs
        v-model="formTab"
        class="app-form-page__tabs"
        :class="{ 'app-form-page__tabs--mobile-bar-hidden': isMobile && !isEmbedded }"
        :show-arrows="isMobile"
      >
        <v-tab>
          {{ $t("public-information") }}
        </v-tab>
        <v-tab :disabled="!documentPermmission">
          {{ $t("documents") }}
        </v-tab>
        <v-tab-item>
          <div v-if="isMobile && !isEmbedded" class="amf-summary" dir="rtl">
            <button
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refPayer')"
            >
              <span class="amf-summary__label">{{ $t('payer') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !payer }"
              >{{ mobilePayerText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="openAmountNumpad">
              <span class="amf-summary__label">{{ $t('amount') }}</span>
              <span class="amf-summary__value">{{ mobileAmountText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="dateSheet = true">
              <span class="amf-summary__label">{{ $t('date') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !date }"
              >{{ date || '—' }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refCurrency')"
            >
              <span class="amf-summary__label">{{ $t('currency') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !currency }"
              >{{ mobileCurrencyText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="openRateNumpad">
              <span class="amf-summary__label">{{ $t('currency-rate') }}</span>
              <span class="amf-summary__value">{{ mobileRateText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              v-if="currency"
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refReceiver')"
            >
              <span class="amf-summary__label">{{ $t('receiver') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !receiver }"
              >{{ mobileReceiverText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row amf-summary__row--tall"
              @click="otherSheet = true"
            >
              <span class="amf-summary__label">{{ $t('otherInputs') }}</span>
              <span class="amf-summary__value amf-summary__value--muted">{{ $t('other-inputs-hint') }}</span>
              <v-icon size="22" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              v-if="documentPermmission"
              type="button"
              class="amf-summary__row"
              @click="formTab = 1"
            >
              <span class="amf-summary__label">{{ $t('documents') }}</span>
              <span class="amf-summary__value">{{
                hasAttachmentFiles
                  ? attachments.length + ' فایل'
                  : $t('no-data-found')
              }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <div class="amf-sr-only" aria-hidden="true">
              <v-text-field v-model="date" :rules="[(v) => !!v || $t('is-required')]" />
              <v-text-field v-model="amount" :rules="[(v) => !!v || $t('is-required')]" />
              <v-text-field
                v-model="currency_amount"
                :rules="[(v) => !!v || $t('is-required')]"
              />
              <autocomplete
                ref="refPayer"
                v-model="payer"
                :rules="[(v) => !!v || $t('is-required')]"
                :title="$t('payer')"
                type="detailTransfer"
                :new-btn="true"
                :value="payer"
              />
              <autocomplete
                ref="refReceiver"
                v-model="receiver"
                :rules="[(v) => !!v || $t('is-required')]"
                :title="$t('receiver')"
                type="detailTransfer"
                :new-btn="true"
                :value="receiver"
              />
              <autocomplete
                ref="refCurrency"
                v-model="currency"
                :rules="[(v) => !!v || $t('is-required')]"
                :title="$t('currency')"
                type="currency"
                :value="currency"
                :init="defaultCurrency"
                @change="changeCurrency"
              />
            </div>
          </div>

          <div v-if="!isMobile || isEmbedded" class="app-form-body">
            <v-container fluid class="app-form-container app-form-container--cards pa-3">
              <v-row class="app-form-card-grid ma-0" dense>
                <!-- Main transfer card -->
                <v-col cols="12" md="7" class="pa-2">
                  <div class="app-form-card">
                    <div class="app-form-card__header">
                      <div class="app-form-card__icon" aria-hidden="true">
                        <v-icon color="primary" size="22">mdi-swap-horizontal</v-icon>
                      </div>
                      <div class="app-form-card__heading">
                        <div class="app-form-card__title">
                          {{ $t("public-information") }}
                        </div>
                        <div class="app-form-card__subtitle">
                          {{ $t("transfer-main-info-hint") }}
                        </div>
                      </div>
                    </div>
                    <div class="app-form-card__body">
                      <v-row class="app-form-fields ma-0">
                        <v-col cols="12" sm="4">
                          <autocomplete
                            v-model="payer"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :title="$t('payer')"
                            type="detailTransfer"
                            :new-btn="true"
                            :value="payer"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <money
                            v-model="amount"
                            :label="$t('amount')"
                            :dense="true"
                            :outlined="true"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :options="options"
                            :value-options="{
                              max: bank
                                ? Number(bank.remain.currency_remain)
                                : 9999999999999999999,
                            }"
                            @SetValueMax="SetValueMaxPrice()"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <template>
                            <v-text-field
                              id="from"
                              v-model="date"
                              :label="$t('date')"
                              :rules="[(v) => !!v || $t('is-required')]"
                              clear-icon="flaticon2-close-cross"
                              outlined
                              dense
                              clearable
                              hide-details="auto"
                            />
                            <date-picker
                              v-model="date"
                              element="from"
                              :locale="$store.state.date_locale"
                              :format="$store.state.date_format"
                              auto-submit
                              :min="$store.state.startDate"
                              :max="$store.state.endDate"
                              :color="
                                this.$vuetify.theme.themes[
                                  this.$vuetify.theme.isDark ? 'dark' : 'light'
                                ].primary
                              "
                              @close="setDate"
                            />
                          </template>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <autocomplete
                            ref="refCurrency"
                            v-model="currency"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :title="$t('currency')"
                            type="currency"
                            :value="currency"
                            :disabled="id ? false : false"
                            :init="defaultCurrency"
                            @change="changeCurrency"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <money
                            v-model="currency_amount"
                            :label="$t('currency-rate')"
                            :dense="true"
                            :outlined="true"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :options="options"
                          />
                        </v-col>
                        <v-col v-if="currency" cols="12" sm="4">
                          <autocomplete
                            v-model="receiver"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :title="$t('receiver')"
                            type="detailTransfer"
                            :new-btn="true"
                            :value="receiver"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </v-col>

                <!-- Other inputs card -->
                <v-col cols="12" md="5" class="pa-2">
                  <div class="app-form-card">
                    <div class="app-form-card__header">
                      <div class="app-form-card__icon" aria-hidden="true">
                        <v-icon color="primary" size="22">mdi-tune-variant</v-icon>
                      </div>
                      <div class="app-form-card__heading">
                        <div class="app-form-card__title">
                          {{ $t("otherInputs") }}
                        </div>
                        <div class="app-form-card__subtitle">
                          {{ $t("other-inputs-hint") }}
                        </div>
                      </div>
                    </div>
                    <div class="app-form-card__body app-form-card__body--stack">
                      <v-row class="app-form-fields ma-0">
                        <v-col cols="12" sm="6">
                          <autocomplete
                            v-model="payerGiven"
                            :title="$t('payerGiven')"
                            type="given"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <autocomplete
                            v-model="receiverGiven"
                            :title="$t('receiverGiven')"
                            type="given"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <autocomplete
                            v-model="branch"
                            :title="$t('branch')"
                            type="branch"
                            :set-init="true"
                            :value="branch"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <tag v-model="tag" :solo="false" :input="tag" :height="25" />
                        </v-col>
                        <v-col cols="12" class="app-form-fields__grow">
                          <description
                            ref="refDescription"
                            v-model="content"
                            class="payment-description"
                            :outlined="true"
                            :rows="4"
                            type="sell"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="app-form-body">
            <v-container class="app-form-container app-form-documents pt-3 pb-2">
              <form-attachment-doc
                ref="refFilePicker"
                v-model="attachments"
                type-prop="transfer"
                :val="id && attachments ? attachments : null"
              />
            </v-container>
          </div>
        </v-tab-item>
      </v-tabs>

      <template v-if="isMobile && !isEmbedded">
        <v-bottom-sheet v-model="otherSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('otherInputs') }}</div>
              <button type="button" class="amf-sheet__back" @click="otherSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-sheet__body--stack">
              <autocomplete
                v-model="payerGiven"
                :title="$t('payerGiven')"
                type="given"
              />
              <autocomplete
                v-model="receiverGiven"
                :title="$t('receiverGiven')"
                type="given"
              />
              <autocomplete
                v-model="branch"
                :title="$t('branch')"
                type="branch"
                :set-init="true"
                :value="branch"
              />
              <tag v-model="tag" :solo="false" :input="tag" :height="25" />
              <description
                ref="refDescription"
                v-model="content"
                class="payment-description"
                :outlined="true"
                :rows="4"
                type="sell"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="otherSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="dateSheet">
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('date') }}</div>
              <button type="button" class="amf-sheet__back" @click="dateSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-mobile-date" dir="rtl">
              <div class="amf-mobile-date__selected">
                <span class="amf-mobile-date__label">{{ $t('date') }}</span>
                <strong class="amf-mobile-date__value">{{ date || '—' }}</strong>
                <button
                  v-if="date"
                  type="button"
                  class="amf-mobile-date__clear"
                  @click="date = null"
                >
                  {{ $t("clear") }}
                </button>
              </div>
              <date-picker
                v-model="date"
                inline
                simple
                auto-submit
                type="date"
                :locale="$store.state.date_locale"
                :format="$store.state.date_format"
                :min="$store.state.startDate"
                :max="$store.state.endDate"
                class="amf-mobile-date__picker"
                color="#7B8BA3"
                @close="setDate"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="dateSheet = false">
                {{ $t("Save") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
          <v-card class="amf-numpad" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-numpad__header">
              <span class="amf-numpad__header-spacer" />
              <div class="amf-numpad__title">{{ numpadLabel }}</div>
              <button type="button" class="amf-sheet__back" @click="numpadOpen = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-numpad__display">
              <div class="amf-numpad__display-label">{{ numpadLabel }}</div>
              <div class="amf-numpad__display-value">{{ numpadDisplay }}</div>
            </div>
            <div class="amf-numpad__pad" dir="ltr">
              <div class="amf-numpad__digits">
                <button
                  v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
                  :key="'d-' + n"
                  type="button"
                  class="amf-numpad__key"
                  @click="numpadDigit(n)"
                >
                  {{ n }}
                </button>
                <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('00')">00</button>
                <button type="button" class="amf-numpad__key" @click="numpadDigit(0)">0</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('000')">000</button>
              </div>
              <div class="amf-numpad__actions">
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(1)">+</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(-1)">-</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadClear">C</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadDot">.</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadBackspace">
                  <v-icon size="20" color="#5B7C99">mdi-backspace-outline</v-icon>
                </button>
              </div>
            </div>
            <div class="amf-numpad__footer">
              <button type="button" class="amf-sheet__btn" @click="confirmNumpad">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>
      </template>

      <div class="app-form-page__actions">
        <div class="app-form-page__actions-inner">
          <v-btn
            v-if="!id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="create"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="update"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            color="primary"
            outlined
            depressed
            class="app-form-action-btn"
            @click="goTo('transferList')"
          >
            {{ $t("transferList") }}
          </v-btn>
        </div>
      </div>
    </v-form>
    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
// import Combobox from '../../components/general/combobox/Combobox'
import autocomplete from "../../components/general/patial/autocomplete";
import Description from "../../components/general/description/Description";
import General from "../../../../Mixins/general";
import FormAttachmentDoc from "../../components/general/FormAttachmentDoc";
export default {
  name: "Transfer",
  components: {
    // Combobox,
    Tag: () => import("../../components/general/Tag/tag"),
    autocomplete,
    FormAttachmentDoc,
    Description,
  },
  mixins: [General],
  props: ["typeForm", "accountProp"],
  data: () => ({
    valid: true,
    dateFormat: "YYYY/MM/DD",
    more: true,
    attachments: null,
    currency: null,
    defaultCurrency: null,
    accountGiven: null,
    bankGiven: null,
    receiver: null,
    payer: null,
    payerGiven: null,
    receiverGiven: null,
    content: null,
    id: null,
    extra: false,
    relations: null,
    snakbar: null,
    snakbarText: null,
    snakbarColor: null,
    sumRelations: null,
    currency_amount: 1,
    tag: null,
    remianBankColor: "blue",
    remianColor: "blue",
    customerId: null,
    bankRemain: null,
    taxStatus: null,
    taxStatusItems: null,
    clearing: null,
    snackbarShow: null,
    amount: 0,
    editPrice: 0,
    type: "receivable",
    NetPrice: 0,
    taxAmount: 0,
    tax: 9,
    discount: 0,
    taxFeilds: null,
    discountFeilds: null,
    account: null,
    options: {
      locale: "en-US",
      prefix: "",
      suffix: "  ",
      precision: 0,
    },
    optionsTax: {
      locale: "en-US",
      prefix: "",
      suffix: "  %  ",
      precision: 0,
    },
    detail: null,
    bank: null,
    remianLable: null,
    description: null,
    date: null,
    render: true,
    isLoading: false,
    formTab: 0,
    otherSheet: false,
    dateSheet: false,
    numpadOpen: false,
    numpadField: null,
    numpadLabel: "",
    numpadDraft: "",
    numpadIsAmount: true,
  }),
  computed: {
    isEmbedded() {
      return !!(this.typeForm || this.accountProp);
    },
    sum() {
      if (this.taxStatus === "notAdd") {
        return (
          parseFloat(this.price) - parseFloat(this.discount) + parseFloat(this.editPrice)
        );
      } else {
        return (
          parseFloat(this.price) - parseFloat(this.discount) + parseFloat(this.taxAmount)
        );
      }
    },
    mobilePayerText() {
      if (!this.payer) return "—";
      return this.payer.name || this.payer.title || "—";
    },
    mobileReceiverText() {
      if (!this.receiver) return "—";
      return this.receiver.name || this.receiver.title || "—";
    },
    mobileCurrencyText() {
      if (!this.currency) return "—";
      return this.currency.name || "—";
    },
    mobileAmountText() {
      if (this.amount === null || this.amount === undefined || this.amount === "") {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.amount, "plain", 0)
        : String(this.amount);
    },
    mobileRateText() {
      if (
        this.currency_amount === null ||
        this.currency_amount === undefined ||
        this.currency_amount === ""
      ) {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.currency_amount, "plain", 0)
        : String(this.currency_amount);
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft || "");
      if (!raw) return "0";
      if (!this.numpadIsAmount || !this.$numberFormat) return raw;
      if (raw.endsWith(".")) {
        const head = raw.slice(0, -1);
        return (head ? this.$numberFormat(head, "plain", 0) : "0") + ".";
      }
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return raw;
      return this.$numberFormat(n, "plain", 0);
    },
    hasAttachmentFiles() {
      return Array.isArray(this.attachments) && this.attachments.length > 0;
    },
  },
  watch: {
    defaultCurrency() {
      if (this.defaultCurrency) {
        this.$refs.refCurrency.setModel(this.defaultCurrency);
        this.currency_amount = this.defaultCurrency.amount;
      }
    },
    bank() {
      if (this.render) {
        this.bankRemainCheck();
      }
    },
    account() {
      this.remianLable = null;
      this.remianColor = "blue";
      if (this.account.remain && this.account.remain.remain > 0) {
        this.remianColor = "pink";
        this.remianLable = this.$t("debtor");
      }
      if (this.account.remain && this.account.remain.remain < 0) {
        this.remianColor = "green";
        this.remianLable = this.$t("creditor");
      }
      if (this.$refs.refRelations) {
        this.$refs.refRelations.initList = false;
        this.relations = null;
        this.$refs.refRelations.clear();
        this.$refs.refRelations.getItems();
      }
    },
    currency() {
      if (!this.id) {
        this.amount = 0;
        this.bankRemain = 0;
        this.currency_amount = this.currency.amount;
      }
    },
    relations() {
      var sum = 0;
      this.relations.map(function (item) {
        sum = parseFloat(sum) + parseFloat(item.price.price);
      });
      this.sumRelations = sum;
    },
    isMobile(val) {
      if (this.isEmbedded) return;
      this.lockPageScroll(!!val);
    },
  },
  beforeMount() {
    if (this.accountProp) {
      this.account = this.accountProp;
    }
    if (this.$route.params.id != null) {
      this.show();
      this.id = this.$route.params.id;
      this.render = false;
    } else {
      this.render = true;
      this.defaultCurrency = this.getDefault("defaultCurrency");
    }
    if (this.$store.state.currency && !this.id) {
      this.currency = this.$store.state.currency;
      this.currency_amount = this.$store.state.currency.amount;
    }
  },
  mounted() {
    this.setDate();
    this.taxStatusItems = [
      {
        value: "add",
        text: this.$t("add-tax-to-price"),
      },
      {
        value: "notAdd",
        text: this.$t("calculate-tax-on-price"),
      },
    ];
    this.taxStatus = "notAdd";
    if (this.isMobile && !this.isEmbedded) {
      this.lockPageScroll(true);
    }
  },
  beforeDestroy() {
    this.lockPageScroll(false);
  },
  methods: {
    openMobileAutocomplete(refName) {
      const ref = this.$refs[refName];
      if (!ref || typeof ref.openMobileDialog !== "function") return;
      if (ref.disabled) return;
      ref.openMobileDialog();
    },
    lockPageScroll(lock) {
      const value = lock ? "hidden" : "";
      const overscroll = lock ? "none" : "";
      document.documentElement.style.overflow = value;
      document.documentElement.style.overscrollBehavior = overscroll;
      document.body.style.overflow = value;
      document.body.style.overscrollBehavior = overscroll;
      const main = document.querySelector(".v-main");
      const wrap = document.querySelector(".v-main__wrap");
      if (main) {
        main.style.overflow = value;
        main.style.overscrollBehavior = overscroll;
      }
      if (wrap) {
        wrap.style.overflow = value;
        wrap.style.overscrollBehavior = overscroll;
      }
    },
    openAmountNumpad() {
      this.numpadField = "amount";
      this.numpadLabel = this.$t("amount");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.amount !== null && this.amount !== undefined && this.amount !== ""
          ? String(this.amount)
          : "";
      this.numpadOpen = true;
    },
    openRateNumpad() {
      this.numpadField = "currency_amount";
      this.numpadLabel = this.$t("currency-rate");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.currency_amount !== null &&
        this.currency_amount !== undefined &&
        this.currency_amount !== ""
          ? String(this.currency_amount)
          : "";
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    confirmNumpad() {
      if (this.numpadField) {
        const raw = String(this.numpadDraft || "").trim();
        const parsed = raw === "" ? 0 : parseFloat(raw);
        this[this.numpadField] = Number.isNaN(parsed) ? 0 : parsed;
        if (this.numpadField === "amount") {
          this.bankRemainCheck();
        }
      }
      this.numpadField = null;
      this.numpadOpen = false;
    },
    changeCurrency() {
      this.bankRemain = 0;
      this.currency_amount = this.currency.amount;
      this.options = {
        locale: "en-US",
        prefix: "",
        suffix: this.currency.name,
        precision: this.currency.decimal,
      };
    },
    SetValueMaxPrice() {
      if (this.render && this.type === "pay") {
        this.amount = 0;
      }
    },
    bankRemainCheck() {
      if (this.render && this.bank && this.type === "pay") {
        if (this.$route.params.id != null) {
          this.bankRemain =
            parseFloat(this.bank.remain.currency_remain) - parseFloat(this.amount);
        } else {
          this.bankRemain =
            parseFloat(this.bank.remain.currency_remain) - parseFloat(this.amount);
        }
        this.remianBankColor = "grey";
        if (this.bankRemain > 0) {
          this.remianBankColor = "green";
        }
        if (this.bankRemain < 0) {
          this.remianBankColor = "pink";
        }
        if (
          this.bank &&
          parseFloat(this.amount) > parseFloat(this.bank.remain.currency_remain)
        ) {
          this.amount = 0;
          this.bankRemain =
            parseFloat(this.bank.remain.currency_remain) - parseFloat(this.amount);
          this.snakbarText = this.$t("alert-pay-amount-bigger-than-bank-remain");
          this.snakbarColor = "pink";
          this.snakbar = true;
        }
      }
    },
    SetValueMaxDiscount() {
      this.discount = this.price;
    },
    create() {
      if (!this.$refs.form.validate()) return false;
      var app = this;
      app.isLoading = true;
      app.$axios
        .post(this.$endpoint + "transfer", this.$data)
        .then((res) => {
          res = res.data;
          app.snakbarText = app.$t("registered");
          app.snakbarColor = "green";
          app.snakbar = true;
          if (app.$refs.refFilePicker !== undefined && app.attachments) {
            app.$refs.refFilePicker.create(res.data.id);
          }
          app.$emit("close", app.$route);

          app.goTo("transferList");
        })
        .catch((err) => {
          if (err.response && err.response.status === 409) {
            this.$store.commit("SET_PLAN_DIALOG", true);
          }
          this.serverErrorHandeler(err);
        })
        .finally(() => {
          this.isLoading = false;
          // this.clearForm()
        });
    },
    update() {
      var app = this;
      app.isLoading = true;
      app.$axios
        .put(this.$endpoint + "transfer/" + this.$route.params.id, this.$data)
        .then((res) => {
          res = res.data;
          if (app.$refs.refFilePicker !== undefined) {
            app.$refs.refFilePicker.create(res.data.id);
          }
          app.$emit("close", app.$route);
          app.goTo("transferList");
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.isLoading = false));
    },
    show() {
      var app = this;
      app.clearForm();
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "transfer/" + this.$route.params.id + "/edit", {
          params: {
            id: this.$route.params.id,
          },
        })
        .then((res) => {
          app.$nextTick(() => {
            res = res.data;
            app.currency = res.currency;
            app.currency_amount = res.currency_amount;
            app.type = res.type;
            app.id = res.id;
            app.attachments = res.attachments;
            app.amount = res.amount;
            app.payer = res.payer;
            app.payerGiven = res.payer_given;
            app.receiver = res.receiver;
            app.receiverGiven = res.receiver_given;
            app.date = res.date;
            app.content = res.content;
            if (res.tags) {
              app.tag = res.tags;
              app.more = true;
            }
            app.render = true;
          });
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    clearForm() {
      this.more = true;
      this.defaultCurrency = null;
      this.attachments = null;
      this.accountGiven = null;
      this.bankGiven = null;
      this.id = null;
      this.extra = false;
      this.relations = null;
      this.snakbar = null;
      this.snakbarText = null;
      this.snakbarColor = null;
      this.sumRelations = null;
      this.currency_amount = 1;
      this.tag = null;
      this.remianBankColor = "blue";
      this.remianColor = "blue";
      this.customerId = null;
      this.bankRemain = null;
      this.taxStatus = null;
      this.clearing = null;
      this.snackbarShow = null;
      this.amount = 0;
      this.editPrice = 0;
      this.type = null;
      this.NetPrice = 0;
      this.taxAmount = 0;
      this.tax = 9;
      this.discount = 0;
      this.taxFeilds = null;
      this.discountFeilds = null;
      this.account = null;
      this.detail = null;
      this.bank = null;
      this.remianLable = null;
      this.description = null;
      this.date = null;
    },
  },
};
</script>

<style scoped></style>
